import { useMemo, useState } from 'react';
import { addStudentNote } from '../runtime/notesStore';
import { noteVisibilityOptions } from '../runtime/noteVisibility';
import { useStudentNotes } from '../runtime/useStudentNotes';
import type { RuntimeActorRole, StudentNoteType, StudentNoteVisibility } from '../runtime/types';

const noteTypeLabels: Record<StudentNoteType, string> = {
  general: 'Umum',
  learning: 'Pembelajaran',
  behavior: 'Perilaku belajar',
  parent: 'Catatan orang tua',
  follow_up: 'Tindak lanjut',
  achievement: 'Pencapaian',
  concern: 'Perlu perhatian',
};

const visibilityLabels: Record<StudentNoteVisibility, string> = {
  staff_only: 'Internal guru/admin',
  guardian_shared: 'Dibagikan ke orang tua',
  student_shared: 'Dibagikan ke siswa',
  all_authorized: 'Semua yang berwenang',
};

export function StudentNotesPanel(props: {
  studentId: string;
  viewerRole: RuntimeActorRole;
  authorName: string;
  allowCreate?: boolean;
  defaultNoteType?: StudentNoteType;
  subjectCode?: string;
  title?: string;
}) {
  const notes = useStudentNotes(props.studentId, props.viewerRole);
  const visibilityOptions = useMemo(() => noteVisibilityOptions(props.viewerRole), [props.viewerRole]);
  const [body, setBody] = useState('');
  const [noteType, setNoteType] = useState<StudentNoteType>(props.defaultNoteType ?? (props.viewerRole === 'guardian' ? 'parent' : 'learning'));
  const [visibility, setVisibility] = useState<StudentNoteVisibility>(visibilityOptions[0]?.value ?? 'all_authorized');

  function submit() {
    const text = body.trim();
    if (!text) return;
    addStudentNote({
      studentId: props.studentId,
      authorRole: props.viewerRole,
      authorName: props.authorName,
      body: text,
      visibility,
      noteType,
      ...(props.subjectCode ? { subjectCode: props.subjectCode } : {}),
    });
    setBody('');
  }

  return (
    <section className="card notes-panel">
      <div>
        <h2>{props.title ?? 'Catatan terintegrasi'}</h2>
        <p className="muted">Catatan ditampilkan sesuai hak akses. History catatan tidak tercampur dengan nilai/mastery.</p>
      </div>

      {props.allowCreate && (
        <div className="note-composer">
          <textarea
            value={body}
            onChange={(event) => setBody(event.target.value)}
            rows={3}
            maxLength={1000}
            placeholder={props.viewerRole === 'guardian' ? 'Contoh: Hari ini Andi mengatakan masih bingung saat meminjam puluhan…' : 'Tulis observasi atau tindak lanjut…'}
          />
          <div className="note-composer__controls">
            <label>Jenis
              <select value={noteType} onChange={(event) => setNoteType(event.target.value as StudentNoteType)}>
                {Object.entries(noteTypeLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </label>
            <label>Siapa yang dapat melihat
              <select value={visibility} onChange={(event) => setVisibility(event.target.value as StudentNoteVisibility)}>
                {visibilityOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </label>
            <button className="btn btn--primary" type="button" disabled={!body.trim()} onClick={submit}>Simpan catatan</button>
          </div>
          <p className="muted note-help">{visibilityOptions.find((item) => item.value === visibility)?.help}</p>
        </div>
      )}

      <div className="notes-list">
        {notes.map((note) => (
          <article className="note-item" key={note.id}>
            <div className="note-item__header">
              <div><strong>{note.authorName}</strong><span className="pill">{noteTypeLabels[note.noteType]}</span></div>
              <time className="muted" dateTime={note.createdAt}>{new Date(note.createdAt).toLocaleString('id-ID', { dateStyle: 'medium', timeStyle: 'short' })}</time>
            </div>
            <p>{note.body}</p>
            <div className="note-item__meta muted"><span>{visibilityLabels[note.visibility]}</span>{note.subjectCode && <span>{note.subjectCode}</span>}{note.skillCode && <span>{note.skillCode}</span>}</div>
          </article>
        ))}
        {notes.length === 0 && <p className="muted">Belum ada catatan yang dapat Anda lihat.</p>}
      </div>
    </section>
  );
}
